/**
 * product-bundle.js
 *
 * Developed by Artie Codes for Stone & Wood
 * 
 * Handles bundle product pages using the same custom dropdown pattern
 * as the legacy BYOC (sections/product--byoc.liquid + product-byoc.js):
 *
 *   1. Clicking .product-swatch__label toggles .product-swatch__wrapper.selected,
 *      which (via existing theme CSS) shows the dropdown.
 *   2. Clicking a .product-swatch__dropdown-item sets the hidden input's value
 *      to the variant's GID, replaces the label text with the option's label,
 *      closes the dropdown, and populates the corresponding media-grid slot.
 *   3. Dropdown labels and images are hydrated from the Storefront API via a
 *      single batched call on page load, cached in sessionStorage for repeat
 *      visits within the same browser session.
 *   4. The add-to-cart button is gated on every choice group having a
 *      selection. Fixed components that are sold out disable the bundle
 *      entirely (no customer recovery).
 *
 * The cart-add flow uses Shopify's native product form submission
 * (product-form.js). The hidden inputs have name="properties[...]"
 * so their values are submitted as line-item properties on the master line
 * item. The cart transform function reads those properties and expands.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Storefront API token resolution
  // ---------------------------------------------------------------------------

  function getStorefrontToken() {
    var picker = document.querySelector('[data-bundle-picker][data-storefront-token]');
    if (picker) return picker.getAttribute('data-storefront-token');

    var meta = document.querySelector('meta[name="shopify-storefront-api-token"]');
    if (meta && meta.content) return meta.content;

    if (window.Shopify && window.Shopify.storefrontToken) {
      return window.Shopify.storefrontToken;
    }

    return null;
  }

  var STOREFRONT_API_VERSION = '2024-10';

  function storefrontEndpoint() {
    return '/api/' + STOREFRONT_API_VERSION + '/graphql.json';
  }

  // ---------------------------------------------------------------------------
  // Session cache
  // ---------------------------------------------------------------------------

  // v2: cached entry shape now carries linked-case metadata (see STATIC_FIELDS).
  var CACHE_KEY_PREFIX = 'bundle-picker-cache-v2:';

  // Only STATIC display data is cached. Volatile stock/availability fields
  // (availableForSale, quantityAvailable, caseAvailable) are NEVER cached — they
  // are always read fresh from the network so inventory limits and sold-out
  // state can't be served stale (critical at low stock, e.g. 2 left).
  var STATIC_FIELDS = ['label', 'image', 'caseLinked', 'caseVariantId', 'caseUrl', 'caseTitle'];

  function cacheKeyForGids(gids) {
    return CACHE_KEY_PREFIX + gids.slice().sort().join('|');
  }

  function readCache(gids) {
    try {
      var raw = sessionStorage.getItem(cacheKeyForGids(gids));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;

      var map = new Map();
      Object.keys(parsed).forEach(function (id) { map.set(id, parsed[id]); });
      return map;
    } catch (e) {
      return null;
    }
  }

  function writeCache(gids, map) {
    if (!map || map.size === 0) return;
    try {
      var obj = {};
      map.forEach(function (value, key) {
        var stat = {};
        STATIC_FIELDS.forEach(function (f) {
          stat[f] = value[f] != null ? value[f] : null;
        });
        obj[key] = stat;
      });
      sessionStorage.setItem(cacheKeyForGids(gids), JSON.stringify(obj));
    } catch (e) {
      // Quota exceeded, sessionStorage disabled, etc. Fail silently.
    }
  }

  // ---------------------------------------------------------------------------
  // Hydration
  // ---------------------------------------------------------------------------

  function fetchVariantDisplayData(gids) {
    // Always fetch fresh — stock/availability must never be served from cache.
    // The cache is used only as a resilience fallback (no token / network error),
    // in which case stock is unknown and treated as unlimited.
    var token = getStorefrontToken();
    if (!token) {
      console.warn('[bundle-picker] No Storefront API token found — labels/stock unavailable.');
      return Promise.resolve(readCache(gids) || new Map());
    }

    var query = [
      'query($ids: [ID!]!) {',
      '  nodes(ids: $ids) {',
      '    ... on ProductVariant {',
      '      id',
      '      title',
      '      availableForSale',
      '      quantityAvailable',
      '      image { url altText }',
      '      product {',
      '        title',
      '        caseVariant: metafield(namespace: "custom", key: "case_variant") {',
      '          value',
      '          reference {',
      '            ... on ProductVariant {',
      '              id',
      '              availableForSale',
      '              product { title onlineStoreUrl }',
      '            }',
      '          }',
      '        }',
      '      }',
      '    }',
      '  }',
      '}'
    ].join('\n');

    return fetch(storefrontEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ query: query, variables: { ids: gids } })
    })
      .then(function (resp) {
        if (!resp.ok) throw new Error('Storefront API HTTP ' + resp.status);
        return resp.json();
      })
      .then(function (data) {
        if (data.errors) {
          console.warn('[bundle-picker] Storefront API returned errors:', data.errors);
        }
        var nodes = (data && data.data && data.data.nodes) || [];
        var map = new Map();
        nodes.forEach(function (node) {
          if (!node || !node.id) return;
          var productTitle = node.product && node.product.title || '';
          var variantTitle = node.title || '';
          var label = productTitle;
          if (variantTitle && variantTitle !== 'Default Title') {
            label = label ? (productTitle + ' — ' + variantTitle) : variantTitle;
          }
          var caseMeta = node.product && node.product.caseVariant;
          var caseRef = caseMeta && caseMeta.reference;
          map.set(node.id, {
            label: trimLabel(label) || node.id,
            image: node.image || null,
            availableForSale: node.availableForSale !== false,
            // Volatile — never cached:
            quantityAvailable: (typeof node.quantityAvailable === 'number') ? node.quantityAvailable : null,
            caseAvailable: caseRef ? caseRef.availableForSale !== false : false,
            // Static — cached:
            // caseLinked = the case_variant metafield is SET (even if the
            // referenced variant is deleted). Distinguishes "not a carton"
            // (unset → behave as before) from "carton, case unavailable" (→ block).
            caseLinked: !!caseMeta,
            caseVariantId: caseRef ? caseRef.id : null,
            caseTitle: caseRef && caseRef.product ? caseRef.product.title : null,
            caseUrl: caseRef && caseRef.product ? caseRef.product.onlineStoreUrl : null
          });
        });
        writeCache(gids, map);
        return map;
      })
      .catch(function (err) {
        console.warn('[bundle-picker] Variant hydration failed:', err);
        // Resilience only: cached static data; stock unknown → unlimited.
        return readCache(gids) || new Map();
      });
  }

  /**
   * Paint hydrated display data onto the DOM. Also marks sold-out options
   * with an .unavailable class (matches existing theme styling pattern).
   */
  function applyDisplayData(picker, displayMap) {
    // Dropdown options
    picker.querySelectorAll('[data-bundle-option]').forEach(function (opt) {
      var gid = opt.getAttribute('data-variant-gid');
      var data = displayMap.get(gid);
      if (!data) return;

      if (data.availableForSale === false) {
        opt.textContent = data.label + ' — Sold out';
        opt.classList.add('unavailable');
        opt.setAttribute('data-sold-out', '');
      } else {
        opt.textContent = data.label;
      }
    });

    // Fixed-entry titles
    picker.querySelectorAll('[data-bundle-fixed] [data-variant-gid]').forEach(function (li) {
      var gid = li.getAttribute('data-variant-gid');
      var data = displayMap.get(gid);
      if (!data) return;

      var titleEl = li.querySelector('[data-variant-title]');
      if (titleEl) titleEl.textContent = data.label;

      if (data.availableForSale === false) {
        li.setAttribute('data-sold-out', '');
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Dropdown interaction (click-to-toggle, click-to-select, outside-close)
  // ---------------------------------------------------------------------------

  function closeAllDropdowns(picker, exceptWrapper) {
    picker.querySelectorAll('.product-swatch__wrapper').forEach(function (w) {
      if (w === exceptWrapper) return;
      w.classList.remove('selected');
    });
  }

  /**
   * Wire dropdown open/close. Clicking the label toggles the wrapper's
   * .selected class (which the existing theme CSS uses to show the
   * dropdown and rotate the caret).
   */
  function wireDropdowns(picker) {
    var wrappers = picker.querySelectorAll('[data-bundle-choice]');

    wrappers.forEach(function (wrapper) {
      var label = wrapper.querySelector('[data-bundle-label]');
      if (!label) return;

      label.addEventListener('click', function (e) {
        e.stopPropagation();
        var wasOpen = wrapper.classList.contains('selected');
        closeAllDropdowns(picker);
        if (!wasOpen) {
          wrapper.classList.add('selected');
          wrapper.classList.add('active');
        }
      });
    });

    // Click outside closes dropdowns
    document.addEventListener('click', function (e) {
      if (!picker.contains(e.target)) {
        closeAllDropdowns(picker);
      }
    });
  }

  /**
   * Wire option selection. Clicking a dropdown item:
   *   - Sets the hidden input's value to the option's GID
   *   - Replaces the label text with the option's label
   *   - Closes the dropdown
   *   - Fires a 'change' event on the hidden input so other listeners
   *     (gating, image grid) can react without caring about the UI.
   */
  function wireOptionSelection(picker) {
    picker.querySelectorAll('[data-bundle-choice]').forEach(function (wrapper) {
      var input = wrapper.querySelector('[data-bundle-select]');
      var labelText = wrapper.querySelector('[data-bundle-label-text]');
      if (!input || !labelText) return;

      wrapper.querySelectorAll('[data-bundle-option]').forEach(function (opt) {
        opt.addEventListener('click', function (e) {
          e.stopPropagation();
          if (opt.hasAttribute('data-sold-out')) return;
          if (opt.hasAttribute('data-stock-exhausted')) return;

          var gid = opt.getAttribute('data-variant-gid');
          input.value = gid;
          labelText.textContent = opt.textContent.trim();
          wrapper.classList.remove('selected');
          wrapper.classList.add('active');

          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Hero / grid toggle
  // ---------------------------------------------------------------------------

  function findMedia(picker) {
    var sectionId = picker.getAttribute('data-section-id');
    if (!sectionId) return null;
    return document.querySelector(
      '[data-bundle-picker-media][data-section-id="' + sectionId + '"]'
    );
  }

  function updateMediaVisibility(media, inputs) {
    if (!media) return;
    var anyFilled = inputs.some(function (i) { return i.value; });
    var hero = media.querySelector('[data-bundle-hero]');
    var grid = media.querySelector('[data-bundle-grid]');
    if (hero) hero.hidden = anyFilled;
    if (grid) grid.hidden = !anyFilled;
  }

  function wireImageGrid(picker, media, displayMap, inputs) {
    if (!media) return;
    var grid = media.querySelector('[data-bundle-grid]');
    if (!grid) return;

    inputs.forEach(function (input, slotIndex) {
      input.addEventListener('change', function () {
        var slot = grid.querySelector('[data-grid-slot="' + slotIndex + '"]');
        if (!slot) return;

        var gid = input.value;
        if (!gid) {
          slot.innerHTML = '<div class="bundle-picker-media__placeholder" aria-hidden="true"></div>';
          updateMediaVisibility(media, inputs);
          return;
        }

        var data = displayMap.get(gid);
        if (data && data.image && data.image.url) {
          slot.innerHTML =
            '<img class="bundle-picker-media__slot-image" ' +
            'src="' + escapeAttr(data.image.url) + '" ' +
            'alt="' + escapeAttr(data.image.altText || data.label || '') + '" ' +
            'loading="lazy">';
        } else {
          slot.innerHTML =
            '<div class="bundle-picker-media__placeholder bundle-picker-media__placeholder--filled" aria-hidden="true">' +
            escapeHtml((data && data.label) || '') +
            '</div>';
        }
        updateMediaVisibility(media, inputs);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Fixed-component availability
  // ---------------------------------------------------------------------------

  function findSoldOutFixedComponents(picker, displayMap) {
    var soldOut = [];
    picker.querySelectorAll('[data-bundle-fixed] [data-variant-gid]').forEach(function (li) {
      var gid = li.getAttribute('data-variant-gid');
      var data = displayMap.get(gid);
      if (data && data.availableForSale === false) {
        soldOut.push(data.label || gid);
      }
    });
    return soldOut;
  }

  // ---------------------------------------------------------------------------
  // Submit button gating
  // ---------------------------------------------------------------------------

  function wireSubmitGating(picker, inputs, displayMap) {
    var formId = picker.getAttribute('data-form-id');
    var form = formId ? document.getElementById(formId) : null;
    if (!form) return;

    var submit = form.querySelector('[data-bundle-submit]') ||
                 form.querySelector('[type="submit"][name="add"]');
    if (!submit) return;

    var sectionId = picker.getAttribute('data-section-id');
    var statusEl = picker.querySelector('[data-bundle-status]');
    var submitSpan = submit.querySelector('span');
    var defaultLabel = submitSpan ? submitSpan.textContent : '';

    // Dynamic-checkout (Shop Pay) button in the same form would bypass the
    // submit-button block, so we tie its visibility to bundle validity: shown
    // only for a complete, non-identical bundle. Hiding it also closes the
    // express-checkout path for an identical carton.
    var paymentBtn = form.querySelector('.shopify-payment-button') ||
                     form.querySelector('shopify-payment-button');
    function setPayment(show) { if (paymentBtn) paymentBtn.style.display = show ? '' : 'none'; }

    function setLabel(text) { if (submitSpan) submitSpan.textContent = text; }
    function enable() {
      submit.removeAttribute('disabled');
      submit.setAttribute('aria-disabled', 'false');
    }
    function disable() {
      submit.setAttribute('disabled', 'disabled');
      submit.setAttribute('aria-disabled', 'true');
    }

    // A sold-out FIXED component disables the whole bundle (unchanged behaviour).
    var soldOutFixed = displayMap ? findSoldOutFixedComponents(picker, displayMap) : [];
    if (soldOutFixed.length > 0) {
      disable();
      setPayment(false);
      setLabel('Unavailable');
      if (statusEl) {
        statusEl.textContent = soldOutFixed.length === 1
          ? 'Currently unavailable — ' + soldOutFixed[0] + ' is sold out.'
          : 'Currently unavailable — ' + soldOutFixed.length + ' components sold out.';
      }
      picker.querySelectorAll('[data-bundle-label]').forEach(function (label) {
        label.style.pointerEvents = 'none';
        label.style.opacity = '0.5';
      });
      return;
    }

    // --- Identical-carton case mode -----------------------------------------
    // When all four slots resolve to the same beer the bundle is ALWAYS blocked
    // (adding it would cost case − 10%, cheaper than the real case). Instead we
    // add the linked case variant directly, or block ATC if it is unavailable.
    var caseMode = false;       // true ⇒ the CTA adds the case variant
    var caseVariantId = null;
    var caseLabel = '';         // the "Add the X case" button label to restore

    function emitIdentical(identical, caseTitle) {
      document.dispatchEvent(new CustomEvent('bundle:identical', {
        detail: { sectionId: sectionId, identical: !!identical, caseTitle: caseTitle || null }
      }));
    }

    // Single persistent click handler — only acts in case mode, where the button
    // is type="button" (so product-form.js never submits the bundle master).
    submit.addEventListener('click', function (e) {
      if (!caseMode || !caseVariantId) return;   // bundle mode → let the form submit
      e.preventDefault();
      e.stopPropagation();
      if (submit.hasAttribute('disabled')) return;

      // Preferred path: reuse the theme's own product-form submit so the case is
      // added AND the cart drawer re-renders through the exact same, known-good
      // flow every normal product uses. We temporarily point the form's variant
      // input at the case and disable the bundle-selection inputs so they aren't
      // submitted as properties. product-form captures FormData synchronously in
      // its submit handler, so we can restore the form immediately afterwards.
      var idInput = form.querySelector('[name="id"]');
      if (idInput && typeof form.requestSubmit === 'function') {
        var numericId = String(caseVariantId).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
        var prevId = idInput.value;
        var prevIdDisabled = idInput.disabled;
        var reEnable = [];
        inputs.forEach(function (bi) {
          if (!bi.disabled) { bi.disabled = true; reEnable.push(bi); }
        });
        idInput.value = numericId;
        idInput.disabled = false;
        submit.setAttribute('type', 'submit');    // let product-form handle this submit
        try {
          form.requestSubmit(submit);
        } finally {
          submit.setAttribute('type', 'button');  // restore case mode (no native submit)
          idInput.value = prevId;
          idInput.disabled = prevIdDisabled;
          reEnable.forEach(function (bi) { bi.disabled = false; });
        }
        return;
      }

      // Fallback: bespoke add (adds correctly; drawer may need a manual refresh).
      setLabel('Adding…');
      disable();
      addCaseToCart(caseVariantId)
        .then(function () { setLabel(caseLabel); enable(); })
        .catch(function () { setLabel('Could not add — try again'); enable(); });
    });

    function enterIdenticalMode(gid) {
      submit.setAttribute('type', 'button');   // never submit the bundle master
      setPayment(false);                        // and no express-checkout bypass
      var data = displayMap ? displayMap.get(gid) : null;
      var caseTitle = data ? data.caseTitle : null;
      var canAddCase = !!(data && data.caseVariantId && data.caseAvailable);

      if (canAddCase) {
        caseMode = true;
        caseVariantId = data.caseVariantId;
        //caseLabel = 'Add the ' + (caseTitle || 'case') + ' case';
        caseLabel = 'Add to cart';
        enable();
        setLabel(caseLabel);
        if (statusEl) {
          statusEl.textContent = 'Four of the same? Add the ' + (caseTitle || '') + ' case instead.';
        }
      } else {
        caseMode = false;
        caseVariantId = null;
        disable();
        if (data && data.caseVariantId && !data.caseAvailable) {
          setLabel((caseTitle || 'Case') + ' — Sold out');
          if (statusEl) {
            statusEl.textContent = 'The ' + (caseTitle || '') + ' case is currently unavailable — please choose a mix of beers.';
          }
        } else {
          setLabel('Unavailable as a single case');
          if (statusEl) {
            statusEl.textContent = 'Four identical 4-packs must be bought as a case — please choose a mix of beers.';
          }
        }
      }
      emitIdentical(true, caseTitle);
    }

    function enterBundleMode() {
      caseMode = false;
      caseVariantId = null;
      submit.setAttribute('type', 'submit');
    }

    var wasIdentical = false;

    // Identical-carton handling only applies to a genuine mixed carton: two or
    // more choice slots and no fixed components. This avoids misfiring on a
    // single-choice bundle (trivially "identical") or a fixed+choice bundle.
    var identicalEligible = inputs.length >= 2 && !picker.querySelector('[data-bundle-fixed]');

    function evaluate() {
      // Keep per-slot option availability in step with current selections.
      if (displayMap) enforceInventory(picker, inputs, displayMap);

      var identGid = identicalEligible ? allIdenticalGid(inputs) : null;
      // Only treat an identical selection as a carton when the repeated variant
      // actually links a case (custom.case_variant is set). Choice-only bundles
      // with no case link behave exactly as before — full backward compatibility,
      // and the guard is active precisely when case-equivalent pricing is.
      var identData = identGid && displayMap ? displayMap.get(identGid) : null;
      if (identGid && identData && identData.caseLinked) {
        enterIdenticalMode(identGid);
        wasIdentical = true;
        return;
      }

      // Normal bundle mode.
      enterBundleMode();
      setLabel(defaultLabel);
      if (wasIdentical) {
        emitIdentical(false, null);
        wasIdentical = false;
      }

      var allFilled = inputs.every(function (i) { return i.value; });
      if (allFilled) enable(); else disable();
      setPayment(allFilled);

      if (statusEl) {
        var filled = inputs.filter(function (i) { return i.value; }).length;
        statusEl.textContent = (filled === inputs.length)
          ? ''
          : 'Choose ' + (inputs.length - filled) + ' more to continue.';
      }
    }

    inputs.forEach(function (i) { i.addEventListener('change', evaluate); });
    evaluate();
  }

  // ---------------------------------------------------------------------------
  // Utility
  // ---------------------------------------------------------------------------

  // Hardcoded label trimming. Variant titles often carry packaging suffixes
  // (e.g. "Pacific Ale 4 Pack Cans") that clutter the picker — only the
  // product name should be visible. This array lists markers at which to
  // trim labels. The first marker found in the label wins: everything from
  // the match onwards is removed. Add new markers here as the client
  // requests them.
  //
  // Matches are case-sensitive. Order matters only when markers overlap
  // (longer/more-specific entries should come first).
  var LABEL_TRIM_MARKERS = [
    '4 Pack'
  ];

  function trimLabel(label) {
    if (!label) return label;
    for (var i = 0; i < LABEL_TRIM_MARKERS.length; i++) {
      var idx = label.indexOf(LABEL_TRIM_MARKERS[i]);
      if (idx > -1) {
        return label.substring(0, idx).trim();
      }
    }
    return label;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function escapeAttr(str) {
    return escapeHtml(str).replace(/"/g, '&quot;');
  }

  // Returns the shared variant GID when every slot is filled with the SAME
  // variant, else null. Drives the identical-carton case swap.
  function allIdenticalGid(inputs) {
    if (!inputs.length) return null;
    var first = inputs[0].value;
    if (!first) return null;
    for (var i = 1; i < inputs.length; i++) {
      if (inputs[i].value !== first) return null;
    }
    return first;
  }

  // Adds a single case variant to the cart and opens the theme's cart drawer
  // (mirrors product-form.js). Falls back to navigating to /cart when no cart
  // drawer / notification component is present on the page.
  function addCaseToCart(caseVariantId) {
    var numericId = String(caseVariantId).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    // Replicates fetchConfig('javascript') minus Content-Type so the browser
    // sets the multipart boundary for FormData; Accept: javascript makes
    // Shopify return the requested section HTML for a drawer re-render.
    var config = {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
    };

    var formData = new FormData();
    formData.append('id', numericId);
    formData.append('quantity', '1');
    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }).join(','));
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }
    config.body = formData;

    return fetch(addUrl, config)
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) {
          throw new Error(response.description || 'cart/add failed');
        }
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
          if (typeof window.cartdrawerFreeShippingNote === 'function') window.cartdrawerFreeShippingNote();
        } else {
          window.location = cartUrl;
        }
        return response;
      });
  }

  // Each slot = one 4-pack unit, so a variant may be picked in at most
  // `quantityAvailable` slots. Once its stock is claimed by other slots, hide it
  // in the still-empty slots. Untracked stock (null) ⇒ unlimited. A slot's own
  // current selection is always left visible so it can be kept or cleared.
  function enforceInventory(picker, inputs, displayMap) {
    var counts = {};
    inputs.forEach(function (i) {
      if (i.value) counts[i.value] = (counts[i.value] || 0) + 1;
    });

    picker.querySelectorAll('[data-bundle-choice]').forEach(function (wrapper) {
      var input = wrapper.querySelector('[data-bundle-select]');
      var selfVal = input ? input.value : '';

      wrapper.querySelectorAll('[data-bundle-option]').forEach(function (opt) {
        if (opt.hasAttribute('data-sold-out')) return;   // leave sold-out styling intact

        var gid = opt.getAttribute('data-variant-gid');
        var data = displayMap.get(gid);
        var inv = data && typeof data.quantityAvailable === 'number' ? data.quantityAvailable : null;

        var otherCount = (counts[gid] || 0) - (selfVal === gid ? 1 : 0);
        var exhausted = inv !== null && selfVal !== gid && otherCount >= inv;

        if (exhausted) {
          opt.style.display = 'none';
          opt.setAttribute('data-stock-exhausted', '');
        } else if (opt.hasAttribute('data-stock-exhausted')) {
          opt.style.display = '';
          opt.removeAttribute('data-stock-exhausted');
        }
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  function init() {
    var pickers = document.querySelectorAll('[data-bundle-picker]');
    if (!pickers.length) return;

    pickers.forEach(function (picker) {
      var inputs = Array.prototype.slice.call(
        picker.querySelectorAll('input[data-bundle-select]')
      );
      var media = findMedia(picker);

      wireDropdowns(picker);
      wireOptionSelection(picker);

      var raw = picker.getAttribute('data-variant-gids') || '';
      var gids = raw.split(',').map(function (g) { return g.trim(); }).filter(Boolean);

      if (!gids.length) {
        wireSubmitGating(picker, inputs, null);
        return;
      }

      fetchVariantDisplayData(gids).then(function (displayMap) {
        applyDisplayData(picker, displayMap);
        wireImageGrid(picker, media, displayMap, inputs);
        wireSubmitGating(picker, inputs, displayMap);
        updateMediaVisibility(media, inputs);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();